import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import FoOpenCasesColumnRenderer from "./FoOpenCasesColumnRenderer";
import { Button, Label } from "semantic-ui-react";
import { useHistory } from "react-router-dom";
import FoOpenStatusContext from "../FoStatusFilters/FoStatusContext";
import constants from "../../../../../common/constants";
import FoCloseCaseRepository, {
  GetFoOpenCasesRequestObject,
} from "../../../../../common/repository/FoCloseCasesRepoitory";
import FoClosedCases, {
  FoOpenCases,
} from "../../../../../models/FoCloseCase";
import Result from "../../../../../common/repository/Result";
import { toast } from "../../../../common/Toast";
import Table from "../../../../app/Table";
import axios from "axios";
import config from "../../../../../common/repository/config";
import LocalStorage from "../../../../../lib/LocalStorage";

const DEFAULT_PAGE_SIZE = 10;
const FILTER_SCAN_CHUNK = 50;

const normalize = (value?: string) =>
  (value || "").replace(/\s+/g, " ").trim().toLowerCase();

const FoOpenStatusTable = () => {
  const ctx = useContext(FoOpenStatusContext);
  const history = useHistory();
  const { userRole, user, states, district, reportee, claimNumber } = ctx.filters;
  const isInvestigator = userRole === constants.roleTitle.investigator;
  const selectedInvestigatorName = useMemo(() => {
    if (!isInvestigator || !user) return "";

    const found = ctx.options.user.find((opt) => opt.value === user);
    return normalize(found ? String(found.text) : "");
  }, [isInvestigator, user, ctx.options.user]);

  const [downloading, setDownloading] = useState(false);

  const foCloseRepo = useMemo(() => new FoCloseCaseRepository(), []);

  const renderInvestigator = useCallback(
    (model: FoOpenCases): JSX.Element => {
      const investigators = model.getInvestigators();

      if (!investigators.length) {
        return <span>-</span>;
      }

      return (
        <div>
          {investigators.map((investigator, index) => (
            <div
              key={`${investigator.investigator_name}-${index}`}
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "6px",
                margin: "4px 0",
              }}
            >
              <span>{investigator.investigator_name}</span>
              <Label
                size="mini"
                color={
                  investigator.investigator_status === "Case Started"
                    ? "green"
                    : "orange"
                }
                style={{ margin: 0 }}
              >
                {investigator.investigator_status || "Unknown"}
              </Label>
            </div>
          ))}
        </div>
      );
    },
    []
  );

  const columnRenderer = useMemo(
    () =>
      new FoOpenCasesColumnRenderer(
        (model: FoOpenCases) => (
          <Button
            size="small"
            icon
            primary
            onClick={() => history.push(`/case/view/${model.getId()}`)}
          >
            Open
          </Button>
        ),
        (_obj: any) => true,
        renderInvestigator
      ),
    [history, renderInvestigator]
  );

  const [foOpenStatus, setFoOpenStatus] = useState<FoOpenCases[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalDataCount, setTotalDataCount] = useState(0);

  const createRequestObject = useCallback(
    (limit: number, offset: number) => {
      const ro: GetFoOpenCasesRequestObject = { limit, offset };

      if (
        !isInvestigator &&
        user.trim().length !== 0 &&
        userRole.trim().length !== 0
      ) {
        ro.user = { id: user, role: userRole, reportee };
      }

      if (claimNumber.trim().length !== 0) {
        ro.claim_number = claimNumber.trim();
      }

      return ro;
    },
    [userRole, user, reportee, claimNumber, isInvestigator]
  );

  const getLocationFilter = useCallback(() => {
    let state = states;
    let dist = "";

    if (district.trim().length !== 0) {
      try {
        const parsed = JSON.parse(district);
        state = parsed.state || state;
        dist = parsed.district || "";
      } catch {
        dist = district;
      }
    }

    return {
      state: normalize(state),
      district: normalize(dist),
    };
  }, [states, district]);

  const downloadFoOpenCases = useCallback(async () => {
    try {
      setDownloading(true);
      const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN)
      if(!token) toast.error("Token is required");
      const loc = getLocationFilter();

      const request = {
        ...createRequestObject(0, 0),
        type: "open",
        userRole,
        user,
        reportee,
        claim_number: claimNumber.trim(),
        states,
        district,
        state: loc.state,
        districtName: loc.district,
        investigatorName: selectedInvestigatorName,
      };

      const response = await axios.post(
        `${config.apiBaseURL}/case/foCases/downloadExcel`,
        request,
        {
          responseType: "blob",
          headers: {
            "x-access-token": token,
          },
        }
      );

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");

      link.href = url;
      link.download = "FO_Open_Cases.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to download sheet");
    } finally {
      setDownloading(false);
    }
  }, [
    createRequestObject,
    getLocationFilter,
    userRole,
    user,
    reportee,
    claimNumber,
    states,
    district,
    selectedInvestigatorName,
  ]);

  const latestRequest = useRef(0);
  const itemsRef = useRef<FoOpenCases[]>([]);
  itemsRef.current = foOpenStatus;

  const pageSizeRef = useRef(DEFAULT_PAGE_SIZE);
  const rawOffsetRef = useRef(0);
  const exhaustedRef = useRef(false);

  const clientSideFiltering = useMemo(() => {
    const loc = getLocationFilter();

    return Boolean(
      loc.state || loc.district || selectedInvestigatorName
    );
  }, [getLocationFilter, selectedInvestigatorName]);

  const fetchFoOpenStatus = useCallback(
    async (reset: boolean) => {
      if (!reset && exhaustedRef.current) return;

      const requestId = ++latestRequest.current;

      try {
        setLoading(true);

        if (reset) {
          rawOffsetRef.current = 0;
          exhaustedRef.current = false;
        }

        const pageSize = pageSizeRef.current;
        const chunk = clientSideFiltering
          ? FILTER_SCAN_CHUNK
          : pageSize;

        const loc = getLocationFilter();

        const matches = (item: FoOpenCases) =>
          (!loc.state ||
            normalize(item.getInsuredState()) === loc.state) &&
          (!loc.district ||
            normalize(item.getInsuredDistrict()) === loc.district) &&
          (!selectedInvestigatorName ||
            item
              .getInvestigatorNames()
              .some(
                (name) =>
                  normalize(name) === selectedInvestigatorName
              ));

        const collected: FoOpenCases[] = [];
        let apiTotal = 0;

        while (collected.length < pageSize) {
          const res = await foCloseRepo.getFoOpenCases(
            createRequestObject(chunk, rawOffsetRef.current)
          );

          if (requestId !== latestRequest.current) return;

          if (res instanceof Result.Error) {
            toast.error(
              res.message || "Failed to fetch FO open cases status"
            );
            return;
          }

          const batch: FoOpenCases[] = res.data.items || [];
          apiTotal = res.data.totalItemCount || 0;

          rawOffsetRef.current += batch.length;

          collected.push(
            ...(clientSideFiltering ? batch.filter(matches) : batch)
          );

          if (
            batch.length < chunk ||
            rawOffsetRef.current >= apiTotal
          ) {
            exhaustedRef.current = true;
            break;
          }

          if (!clientSideFiltering) break;
        }

        const all = reset
          ? collected
          : [...itemsRef.current, ...collected];

        setFoOpenStatus(all);

        setTotalDataCount(
          clientSideFiltering && exhaustedRef.current
            ? all.length
            : apiTotal
        );
      } catch (error) {
        if (requestId !== latestRequest.current) return;

        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to fetch FO open cases status"
        );
      } finally {
        if (requestId === latestRequest.current) {
          setLoading(false);
        }
      }
    },
    [
      foCloseRepo,
      createRequestObject,
      getLocationFilter,
      selectedInvestigatorName,
      clientSideFiltering,
    ]
  );

  useEffect(() => {
    fetchFoOpenStatus(true);
  }, [fetchFoOpenStatus]);

  const load = useCallback(() => {
    fetchFoOpenStatus(false);
  }, [fetchFoOpenStatus]);

  const onRowsPerPageChange = useCallback(
    (_page: number, rowsPerPage: number) => {
      pageSizeRef.current = rowsPerPage;
      fetchFoOpenStatus(true);
    },
    [fetchFoOpenStatus]
  );

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <Button
          primary
          icon="download"
          content={downloading ? "Downloading..." : "Download Excel"}
          loading={downloading}
          disabled={downloading}
          onClick={downloadFoOpenCases}
        />
      </div>

      <Table
        key={JSON.stringify([
          userRole,
          user,
          states,
          district,
          claimNumber,
        ])}
        renderer={columnRenderer}
        data={foOpenStatus}
        totalDataCount={totalDataCount}
        loading={loading}
        load={load}
        onRowsPerPageChange={onRowsPerPageChange}
        onSearch={() => { }}
        DownloadBtn={false}
        onClear={() => { }}
      />
    </>
  );
};

export default FoOpenStatusTable;